import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { User, Save, Mail, Phone, MapPin, Calendar, GraduationCap, Building, Users, Shield, Briefcase } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getErrorMessage = (error, defaultMessage = "Ocorreu um erro") => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (Array.isArray(detail)) {
      return detail[0]?.msg || defaultMessage;
    }
    if (typeof detail === 'string') {
      return detail;
    }
  }
  return defaultMessage;
};

const getRoleLabel = (role) => {
  const labels = {
    admin: 'Administrador',
    coordinator: 'Coordenador',
    student: 'Aluno',
  };
  return labels[role] || role;
};

const getRoleBadgeColor = (role) => {
  if (role === 'admin') return 'bg-red-900/40 text-red-300 border-red-700';
  if (role === 'coordinator') return 'bg-blue-900/40 text-blue-300 border-blue-700';
  return 'bg-green-900/40 text-green-300 border-green-700';
};

const getSectorLabel = (sector) => {
  const labels = {
    estagio: 'Estágio',
    tecnico: 'Técnico',
    comercial: 'Comercial',
  };
  return labels[sector] || sector;
};

const ProfileSettings = ({ open, onOpenChange, token, user, onProfileUpdate }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    code: "",
    shift: "",
    period: "",
    modality: "",
    start_date: "",
    institution: "",
    education_level: "",
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        address: user.address || "",
        code: user.code || "",
        shift: user.shift || "",
        period: user.period || "",
        modality: user.modality || "",
        start_date: user.start_date || "",
        institution: user.institution || "",
        education_level: user.education_level || "",
      });
    }
  }, [user, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };

      const updateData = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] && formData[key].trim() !== "") {
          updateData[key] = formData[key].trim();
        }
      });

      const response = await axios.put(`${API}/auth/me`, updateData, { headers });

      toast.success("Perfil atualizado com sucesso!");

      if (onProfileUpdate) {
        onProfileUpdate(response.data);
      }

      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      toast.error(getErrorMessage(error, "Erro ao atualizar perfil"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', color: '#e0e0f0' }}>
        <DialogHeader style={{ paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem' }}>
          <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0' }}>
            <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <User size={13} style={{ color: '#60a5fa' }} />
            </div>
            Meu Perfil
          </DialogTitle>
          <DialogDescription style={{ fontSize: '0.73rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", paddingLeft: '2.35rem', marginTop: '0.1rem' }}>
            Atualize suas informações pessoais e acadêmicas
          </DialogDescription>
        </DialogHeader>

        {/* Informações de conta (somente leitura) */}
        {user && (
          <div className="rounded-lg p-4 space-y-3 mb-2" style={{ border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}>
            <h3 style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6a6a9a', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'Inter', sans-serif" }}>Dados da Conta</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 flex-shrink-0" style={{ color: '#6a6a9a' }} />
                <div>
                  <p style={{ fontSize: '0.7rem', color: '#4a4a6a' }}>Perfil de Acesso</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                    {getRoleLabel(user.role)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 flex-shrink-0" style={{ color: '#6a6a9a' }} />
                <div>
                  <p style={{ fontSize: '0.7rem', color: '#4a4a6a' }}>Setor</p>
                  <p style={{ fontSize: '0.82rem', fontWeight: 500, color: '#c0c0d8' }}>{getSectorLabel(user.sector)}</p>
                </div>
              </div>
            </div>
            {user.is_active !== undefined && (
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-red-400'}`} />
                <p style={{ fontSize: '0.73rem', color: '#6a6a9a' }}>
                  Conta {user.is_active ? 'ativa' : 'inativa'}
                </p>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações Básicas */}
          <div className="space-y-4">
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Space Grotesk', sans-serif" }}>Informações Básicas</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label htmlFor="name" className="form-label">
                  <User className="w-4 h-4 inline mr-1" />
                  Nome Completo
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Seu nome completo"
                  className="dialog-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="email" className="form-label">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="seu.email@exemplo.com"
                  className="dialog-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label htmlFor="phone" className="form-label">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Telefone
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  placeholder="(00) 00000-0000"
                  className="dialog-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="code" className="form-label">
                  <Users className="w-4 h-4 inline mr-1" />
                  Matrícula
                </Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => handleChange("code", e.target.value)}
                  placeholder="Número de matrícula"
                  className="dialog-input"
                />
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="address" className="form-label">
                <MapPin className="w-4 h-4 inline mr-1" />
                Endereço
              </Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
                placeholder="Rua, número, bairro, cidade"
                className="dialog-input"
                style={{ backgroundColor: 'white', color: '#1f2937' }}
              />
            </div>
          </div>

          {/* Informações Acadêmicas */}
          <div className="space-y-4">
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Space Grotesk', sans-serif" }}>Informações Acadêmicas</h3>

            <div className="grid grid-cols-3 gap-4">
              <div className="form-group">
                <Label htmlFor="shift" className="form-label">
                  Turno
                </Label>
                <Select
                  value={formData.shift}
                  onValueChange={(value) => handleChange("shift", value)}
                >
                  <SelectTrigger className="form-input">
                    <SelectValue placeholder="Selecione o turno" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Matutino">Matutino</SelectItem>
                    <SelectItem value="Vespertino">Vespertino</SelectItem>
                    <SelectItem value="Noturno">Noturno</SelectItem>
                    <SelectItem value="Integral">Integral</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="period" className="form-label">
                  Período
                </Label>
                <Input
                  id="period"
                  value={formData.period}
                  onChange={(e) => handleChange("period", e.target.value)}
                  placeholder="Ex: 3º Semestre"
                  className="dialog-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="modality" className="form-label">
                  Modalidade
                </Label>
                <Select
                  value={formData.modality}
                  onValueChange={(value) => handleChange("modality", value)}
                >
                  <SelectTrigger className="form-input">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Presencial">Presencial</SelectItem>
                    <SelectItem value="EAD">EAD</SelectItem>
                    <SelectItem value="Híbrido">Híbrido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label htmlFor="start_date" className="form-label">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Data de Ingresso
                </Label>
                <Input
                  id="start_date"
                  value={formData.start_date}
                  onChange={(e) => handleChange("start_date", e.target.value)}
                  placeholder="DD/MM/AAAA"
                  className="dialog-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="education_level" className="form-label">
                  <GraduationCap className="w-4 h-4 inline mr-1" />
                  Nível de Formação
                </Label>
                <Select
                  value={formData.education_level}
                  onValueChange={(value) => handleChange("education_level", value)}
                >
                  <SelectTrigger className="form-input">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ensino Médio">Ensino Médio</SelectItem>
                    <SelectItem value="Técnico">Técnico</SelectItem>
                    <SelectItem value="Graduação">Graduação</SelectItem>
                    <SelectItem value="Pós-Graduação">Pós-Graduação</SelectItem>
                    <SelectItem value="Mestrado">Mestrado</SelectItem>
                    <SelectItem value="Doutorado">Doutorado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="institution" className="form-label">
                <Building className="w-4 h-4 inline mr-1" />
                Instituição de Ensino
              </Label>
              <Input
                id="institution"
                value={formData.institution}
                onChange={(e) => handleChange("institution", e.target.value)}
                placeholder="Nome da instituição"
                className="dialog-input"
                style={{ backgroundColor: 'white', color: '#1f2937' }}
              />
            </div>
          </div>

          {/* Botões */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.875rem', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '0.25rem' }}>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6a6a9a', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.28)', color: '#60a5fa', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
            >
              <Save size={13} />
              {loading ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileSettings;
